/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { EdsIcon, CosmosText, CosmosButton } from "@components/elements/atoms";
import { useMessages } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";
import { AuthBack } from "./AuthBox.back";
import { authBox, authOuter } from "./AuthBox.styles";
import { AuthBoxSignUp } from "./AuthBox.signup";
import { useSearchParams } from "next/navigation";
import { isSameOrigin } from "@cosmos/common-utils";

export interface AuthBoxProps {
	origin: string;
	state?: string;
}

export const AuthBox = (props: AuthBoxProps): React.JSX.Element => {
	const messages = useMessages();
	const searchParams = useSearchParams();
	const state = searchParams.get("state") || searchParams.get("redirectUrl") || "";

	const sameOrigin = isSameOrigin(state);
	const query: { state?: string } = {};
	if (sameOrigin) {
		query.state = state;
	}
	const loginHref = `${props.origin}/login`;
	const loginUrl = new URL(loginHref);

	return (
		<div className={cx("auth-box-outer", authOuter)}>
			<div className={cx("auth-box", authBox)}>
				<VStack justifyContent="center" alignItems="center" gap="eds.dimension.32">
					<EdsIcon icon="EpicGames" size="xl" />
					{/* as="h1": this is the auth page's only heading (no hero); without it the
					    title renders as an h2 and the page has no h1. */}
					<CosmosText as="h1" variant="headingXL">
						{messages["epic.cosmos.auth.title"]}
					</CosmosText>
					<CosmosText variant="paragraphMD" color="eds.text.secondary">
						{messages["epic.cosmos.auth.description"]}
					</CosmosText>
					<CosmosButton
						variant="primary"
						href={{
							host: loginUrl.host,
							pathname: loginUrl.pathname,
							query,
						}}
						target="_self"
						className={css({
							w: "full",
							maxWidth: "full",
							h: "eds.dimension.48",
							paddingBlock: "eds.dimension.12",
							paddingInline: "eds.dimension.20",
						})}
					>
						<CosmosText variant="uiMD">{messages["epic.cosmos.auth.login.button.label"]}</CosmosText>
					</CosmosButton>

					<CosmosText variant="paragraphSM" color="eds.text.secondary">
						{messages["epic.cosmos.auth.signup.description"]} <AuthBoxSignUp {...props} />
					</CosmosText>

					<AuthBack {...props} />
				</VStack>
			</div>
		</div>
	);
};
